// @ts-strict
import React, { useState } from "react";
import { Provider as ReduxProvider } from "react-redux";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import { Helmet } from "@/components/shared/ReactHelmetWrapper";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import { QueryClientProvider } from "@tanstack/react-query";
import "@/fonts";
import Main from "@/components/main/Main";
import "@/index.css";
import "react-loading-skeleton/dist/skeleton.css";

import { getStore } from "@/redux/AppStore";
import ReplicatedErrorBoundary from "@/components/shared/ErrorBoundary";
import { getQueryClient } from "@/query-client";
import { AppStateProvider } from "@/context/app-state";
import { ReduxQueryProvider } from "@/query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ClusterProvider } from "./components/compatibility_matrix/ClusterContext";
import { NavigationObserver } from "./observability/navigation-observer";
import { observeNavigation } from "./observability/faro";

// Called after configStore resolves, before RouterProvider subscribes.
const createAppRouter = () => {
  if (import.meta.env.VITE_BUGSNAG_API_KEY) {
    Bugsnag.start({
      apiKey: import.meta.env.VITE_BUGSNAG_API_KEY,
      appVersion: import.meta.env.VITE_VENDOR_WEB_BUILD_VERSION,
      releaseStage: import.meta.env.VITE_BUGSNAG_RELEASE_STAGE,
      enabledReleaseStages: ["production", "staging"],
      plugins: [new BugsnagPluginReact()],
    });
  }

  const ErrorBoundary =
    Bugsnag.getPlugin("react")?.createErrorBoundary(React) ||
    ReplicatedErrorBoundary;

  const router = createBrowserRouter([
    {
      path: "/*", // Main handles internal routing.
      element: (
        <ClusterProvider>
          {import.meta.env.VITE_FARO_ENDPOINT && <NavigationObserver />}
          <ErrorBoundary
            FallbackComponent={ReplicatedErrorBoundary}
            onError={(event) => {
              event.addMetadata("ui", { source: "error-boundary" });
            }}
          >
            <AppStateProvider>
              <ReduxQueryProvider>
                <Main />
              </ReduxQueryProvider>
            </AppStateProvider>
          </ErrorBoundary>
        </ClusterProvider>
      ),
    },
  ]);

  observeNavigation(router);
  return router;
};

const Root = () => {
  const store = getStore();
  const queryClient = getQueryClient();
  const [router] = useState(createAppRouter);

  return (
    <div className="min-h-full min-w-full flex flex-col flex-1">
      <Helmet>
        <meta httpEquiv="Pragma" content="no-cache" />
        <meta httpEquiv="Expires" content="0" />
        <meta httpEquiv="Content-Security-Policy" content="upgrade-insecure-requests" />
        {import.meta.env.VITE_VENDOR_WEB_CSP && (
          <meta httpEquiv="Content-Security-Policy" content={import.meta.env.VITE_VENDOR_WEB_CSP} />
        )}
        <title>Replicated</title>
      </Helmet>

      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <ReduxProvider store={store}>
          <RouterProvider router={router} />
        </ReduxProvider>
      </QueryClientProvider>
    </div>
  );
};
export default Root;
